<template>
  <div class="cash-sources-stat">
    <div class="stat-menu-abc">
      <v-container fluid>
        <stat-top-filter
          :filial-default="[globalFilialId]"
          last-month
          multiple-filial-select
          @applyFilter="applyFilter"
        >
          <template #afterApply="{ filter }">
            <v-btn
              style="border-radius: 10px"
              height="48px"
              depressed
              color="#D3D5D9"
              :loading="reportDownloading"
              @click="downloadReport(filter)"
            >
              Выгрузить в Excel
            </v-btn>
          </template>
        </stat-top-filter>

        <filials-indicators-list
          v-if="Object.keys(indicators).length"
          class="mb-5"
          :indicators="indicators"
          :loading="loading"
        />

        <loader v-if="loading" />
        <template v-else>
          <div class="stat-menu-abc-table">
            <v-data-table
              v-if="statisticData.length"
              fixed-header
              :headers="headers"
              :page="page"
              :items="statisticData"
              :items-per-page="itemsPerPage"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :footer-props="footerProps"
              :server-items-length="itemsLength"
              class="statistic-datatable headers-nowrap orange-header"
              @update:options="loadItems"
            >
              <template #item="{ item }">
                <tr>
                  <td colspan="2">
                    {{ item.filialName }}
                  </td>
                  <td class="text-center">
                    {{ formatCount(item.acceptedOrdersCount) }}
                  </td>
                  <td class="text-center">
                    {{ item.revenue | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ item.upsellingRevenue | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ item.upsellingRevenuePercentage }}%
                  </td>
                  <td class="text-center">
                    {{ item.avgBill | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ formatAvgConfirmTime(item.avgConfirmTime) }}
                  </td>
                </tr>
                <tr
                  v-for="(source, i) in item.sources"
                  :key="item.filialName + source.source"
                >
                  <td v-if="!i" rowspan="3" class="sources-title">
                    <div>Источники</div>
                  </td>
                  <td>{{ source.name }}</td>
                  <td class="text-center">
                    {{ formatCount(source.acceptedOrdersCount) }}
                  </td>
                  <td class="text-center">
                    {{ source.revenue | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ source.upsellingRevenue | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ source.upsellingRevenuePercentage }}%
                  </td>
                  <td class="text-center">
                    {{ source.avgBill | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ formatAvgConfirmTime(source.avgConfirmTime) }}
                  </td>
                </tr>
              </template>
            </v-data-table>
            <div v-else class="no-data-label">
              Нет записей за выбранный период
            </div>
          </div>
        </template>
      </v-container>
    </div>
  </div>
</template>

<script>
  import StatTopFilter from '@/components/stat/StatTopFilter.vue'
  import Loader from '@/components/loader/Loader'
  import { mapGetters } from 'vuex'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import permissions from '@/mixins/permissions'
  import callCenterFilialsApi from '@/api/statistics/call-center/filials'
  import { getFileNameFromContentDispositionHeader } from '@/util/axios'
  import { formatNumberToThousandths } from '@/util/num-format'
  import FilialsIndicatorsList from '@/components/stat/call-center/FilialsIndicatorsList.vue'
  import { formatSecondsToString } from '@/util/helpers'

  export default {
    name: 'FilialsStatistics',
    components: {
      Loader,
      StatTopFilter,
      FilialsIndicatorsList,
    },
    mixins: [permissions, SnackbarMessage],
    data () {
      return {
        indicators: {},
        loading: false,
        reportDownloading: false,
        footerProps: { 'items-per-page-options': [15, 25, 50, 100, -1] },
        startDate: null,
        endDate: null,
        filialIds: [],
        filialPreset: null,
        itemsLength: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        sortDesc: [],
        headers: [
          { text: 'Филиал', value: 'filialName', align: 'left', sortable: false },
          {
            text: 'Принято заказов',
            value: 'acceptedOrdersCount',
            align: 'center',
          },
          { text: 'Сумма', value: 'revenue', align: 'center' },
          { text: 'Допродажи', value: 'upsellingRevenue', align: 'center' },
          {
            text: '% Допродажи',
            value: 'upsellingRevenuePercentage',
            align: 'center',
            sortable: false,
          },
          { text: 'Средний ЧЕК', value: 'avgBill', align: 'center' },
          { text: 'СВП', value: 'avgConfirmTime', align: 'center' },
        ],
        statisticData: [],
      }
    },
    computed: {
      ...mapGetters({
        globalFilialId: 'branches/getGlobalFilialId',
      }),
    },
    watch: {
      loading (val) {
        if (!val) {
          this.$nextTick(() => {
          // eslint-disable-next-line no-unused-expressions
          document
            .querySelector('.statistic-datatable th')
            ?.setAttribute('colspan', '2')
          })
        }
      },
    },
    methods: {
      formatAvgConfirmTime (data) {
        return formatSecondsToString(data)
      },
      formatCount (value) {
        return formatNumberToThousandths(value)
      },
      optionsChanged (options) {
        if (
          this.page !== options.page ||
          this.itemsPerPage !== options.itemsPerPage
        ) {
          return true
        }

        if (
          this.sortBy.length !== options.sortBy.length ||
          this.sortBy.some((item) => !options.sortBy.includes(item))
        ) {
          return true
        }

        return (
          this.sortDesc.length !== options.sortDesc.length ||
          this.sortDesc.some((item) => !options.sortDesc.includes(item))
        )
      },
      loadItems (data) {
        if (!this.optionsChanged(data)) {
          return
        }

        this.itemsPerPage = data.itemsPerPage
        this.page = data.page
        this.sortBy = data.sortBy
        this.sortDesc = data.sortDesc

        const payload = {
          from: this.startDate,
          to: this.endDate,
          filialIds: this.filialIds,
          filialPreset: this.filialPreset,
          page: data.page,
          limit: data.itemsPerPage,
          sortBy: data.sortBy,
          sortDesc: data.sortDesc,
        }

        this.fetchData(payload)
      },
      applyFilter (filter) {
        this.startDate = filter.from
        this.endDate = filter.to
        this.filialIds = filter.filialId
        this.filialPreset = filter.filialPreset

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
          page: this.page,
          limit: this.itemsPerPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }

        this.fetchData(payload)
      },
      async fetchData (data) {
        this.loading = true
        this.statisticData = []

        Promise.all([
          callCenterFilialsApi.fetchFilialsStatistics(data),
          callCenterFilialsApi.fetchFilialsIndicators(data),
        ])
          .then((responses) => {
            this.indicators = responses[1].data
            this.statisticData = responses[0].items
            this.itemsLength = responses[0].meta.total
            this.itemsPerPage = responses[0].meta.limit
          })
          .catch((err) => this.showErrorFromResponse(err))
          .finally(() => {
            this.loading = false
          })
      },
      async downloadReport (filter) {
        this.reportDownloading = true

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
        }

        try {
          const response = await callCenterFilialsApi.exportFilialsStatistics(
            payload,
          )
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(response.data)
          link.setAttribute(
            'download',
            getFileNameFromContentDispositionHeader(response),
          )
          document.body.appendChild(link)
          link.click()
          link.remove()
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.reportDownloading = false
      },
    },
  }
</script>

<style lang="less" scoped>
@import 'src/assets/styles/statistic.less';

.statistic-datatable {
  ::v-deep {
    .v-data-footer {
      display: flex;
      margin-right: 0;
    }
  }
}

.sources-title {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
  padding: 0 !important;
  border: none !important;
  border-left: 1px solid #e2d9d9 !important;
  border-top: 1px solid #e2d9d9 !important;
  font-size: 18px !important;
  color: #c2c6d6;
  background-color: #fff;
}
</style>
